import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretches the button to the full width of its container. */
  fullWidth?: boolean;
}

const BASE =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-pill font-display font-extrabold ' +
  'transition-colors transition-transform duration-150 active:scale-[0.98] ' +
  'disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100 ' +
  'motion-reduce:transition-none motion-reduce:active:scale-100 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hc-accent/70 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-hc-surface';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-hc-accent text-hc-cream text-cta font-extrabold shadow-glow enabled:hover:bg-hc-accent-hover',
  secondary:
    'bg-hc-overlay text-hc-cream border border-white/10 enabled:hover:bg-white/10',
  ghost: 'bg-transparent text-hc-cream/80 enabled:hover:bg-white/10',
  danger: 'bg-rose-600 text-white enabled:hover:bg-hc-accent-strong',
};

const SIZES: Record<ButtonSize, string> = {
  md: 'h-11 px-5',
  lg: 'h-14 px-6',
};

/**
 * Cream on lipstick is 3.8:1, so the primary label is always bold and 19px
 * (`text-cta`, WCAG large text) in both sizes; the other variants step with
 * the size. src/theme/accentContrast.test.ts guards the rule.
 */
const TEXT_SIZES: Record<ButtonVariant, Record<ButtonSize, string>> = {
  primary: { md: '', lg: '' },
  secondary: { md: 'text-base', lg: 'text-lg' },
  ghost: { md: 'text-base', lg: 'text-lg' },
  danger: { md: 'text-base', lg: 'text-lg' },
};

/**
 * Class string of a `Button`, for links that must look like one (an `<a>` or
 * router `<Link>` cannot be nested in a `<button>`).
 */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
} = {}): string {
  return cx(
    BASE,
    VARIANTS[variant],
    SIZES[size],
    TEXT_SIZES[variant][size],
    fullWidth && 'w-full',
    className
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      type = 'button',
      className,
      ...rest
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={buttonClassName({ variant, size, fullWidth, className })}
        {...rest}
      />
    );
  }
);
